import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { TILE_SIZE } from '@shared/constants';
import { getAreasAtTile } from '@shared/utils/areaUtils';
import { getSurfaceById } from '../../sprites/SurfaceSpriteManager';

/**
 * Efectos ambientales del mapa: clasificación de tiles de agua (isWaterTile), factor de "calma" del
 * agua por bloque/esquina para el tint del oleaje (blockCalmFactor/cornerCalmFactor/waterCalmFactor)
 * y el efecto de interior bajo techo (entityUnderRoof/updateInteriorEffect/roofIntersectsInterior:
 * atenúa los roofs cuando el player entra a un edificio). Extraído de GameScene (god-object). Los
 * statics puros (grayCalmTint/isShoreSurfaceLayer) quedan en GameScene y se referencian como
 * `GameScene.*`. El resto vía `this.scene.*`. Ver docs/WATER_AND_BOATS.md.
 */
export class MapAmbientController {
  constructor(private scene: GameScene) {}

  public isWaterTile(spriteId: string, layerName: string): boolean {
    // El agua siempre está en la capa 1 (layer1_ground según el usuario)
    if (layerName !== 'layer1_ground') {
      return false;
    }
    
    // Detectar por sprite ID (ej: "water", "water1", etc.)
    const lowerSpriteId = spriteId.toLowerCase();
    if (lowerSpriteId.includes('water')) {
      return true;
    }
    
    // Verificar si es un surface de tipo "water"
    const surface = getSurfaceById(spriteId);
    if (surface && surface.type === 'water') {
      return true;
    }
    
    return false;
  }

  public blockCalmFactor(bx: number, by: number): number {
    if (!this.scene.waterTileSet.has(`${bx},${by}`)) return 0;
    return this.waterCalmFactor(bx, by);
  }

  /**
   * Calma en la esquina superior-izquierda del bloque cuyo tile origen es (bx,by):
   * promedio de los 4 bloques que comparten esa esquina de la grilla. Bloques
   * adyacentes evalúan la MISMA esquina con los MISMOS 4 vecinos, así que el valor
   * coincide en los bordes compartidos y el tint por-vértice interpola sin costuras.
   */
  public cornerCalmFactor(bx: number, by: number): number {
    return (
      0.25 *
      (this.blockCalmFactor(bx - 4, by - 4) +
        this.blockCalmFactor(bx, by - 4) +
        this.blockCalmFactor(bx - 4, by) +
        this.blockCalmFactor(bx, by))
    );
  }

  public waterCalmFactor(tx: number, ty: number): number {
    // Agua de la línea de costa (bloque con costa/tierra encima) → bajío pleno (cyan),
    // pegado a la orilla. Al ser el borde difuminado del pasto el que la tapa, el cyan se
    // ve en la LÍNEA de agua (natural), no un bloque tierra adentro.
    if (this.scene.shoreBlockSet.has(`${tx},${ty}`)) return 0.06;
    // Agua abierta: el bajío DEGRADA hacia el mar. Anillo r → calma r/(ANCHO+1); a más
    // anillos, más ancha la transición cyan→profundo. Fuera de ANCHO → mar profundo (1).
    const ANCHO_ANILLOS = 0;
    for (let r = 1; r <= ANCHO_ANILLOS; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // solo el borde del anillo
          if (this.scene.shoreBlockSet.has(`${tx + dx * 4},${ty + dy * 4}`)) {
            return Math.max(0.06, r / (ANCHO_ANILLOS + 1));
          }
        }
      }
    }
    return 1.0;
  }

  /**
   * Alimenta el minimapa cada frame con la posición del player local, los NPCs y los
   * otros players visibles. Construye listas livianas (counts limitados por AOI).
   */
  /**
   * True si los pies de una entidad (world px) caen en un área `interior` (bajo techo de
   * casa). Se usa para apagar su sombra con fade. Barato: pocas áreas por mapa.
   */
  public entityUnderRoof(footX: number, footY: number): boolean {
    const areas = this.scene.currentMap?.areas;
    if (!areas) return false;
    const tx = Math.floor(footX / TILE_SIZE);
    const ty = Math.floor(footY / TILE_SIZE);
    return getAreasAtTile(areas, tx, ty).some((a) => a.interior);
  }

  /**
   * Efecto "entrar a una casa": si el player está en un área `interior`, oscurece todo
   * alrededor (LightingSystem), revela el interior y atenúa los techos de esa casa. Todo
   * con un ease sutil (fade in/out). Client-only. Ver docs/AREAS.md.
   */
  public updateInteriorEffect(delta: number): void {
    // Target: 1 si el player está parado en un área `interior`, 0 si no.
    let target = 0;
    const render = this.scene.localSessionId ? this.scene.playerObjects.get(this.scene.localSessionId) : undefined;
    if (render && this.scene.currentMap?.areas) {
      const ptx = Math.round(render.currentWorldX / TILE_SIZE - 0.5);
      const pty = Math.round(render.currentWorldY / TILE_SIZE - 0.5);
      const interiorAreas = getAreasAtTile(this.scene.currentMap.areas, ptx, pty).filter(
        (a) => a.interior
      );
      if (interiorAreas.length > 0) {
        target = 1;
        // Congelar los rects del interior actual (world px) mientras estamos dentro.
        const rects: Array<{ x: number; y: number; w: number; h: number }> = [];
        for (const a of interiorAreas) {
          for (const r of a.rects) {
            rects.push({
              x: r.x * TILE_SIZE,
              y: r.y * TILE_SIZE,
              w: r.w * TILE_SIZE,
              h: r.h * TILE_SIZE,
            });
          }
        }
        this.scene.interiorRevealRects = rects;
      }
    }

    // Ease LINEAL, fades lentos/suaves tanto al entrar (overlay de oscuridad + atenuar techo)
    // como al salir (el techo se re-dibuja de a poco). `step` normalizado por delta (ms).
    // Al entrar (fade-in) techo y overlay van juntos; al salir (fade-out) el OVERLAY se
    // apaga un poquito más rápido que el techo, así no queda tenue colgado después de que
    // el techo ya se ve lleno.
    const inStep = Math.min(1, 0.003 * delta); // ~0.05/frame @60fps → ~0.33s (fade-in)
    const roofOutStep = Math.min(1, 0.0025 * delta); // ~0.042/frame → ~0.40s (fade-out techo)
    const overlayOutStep = Math.min(1, 0.0038 * delta); // ~0.063/frame → ~0.26s (fade-out overlay, más corto)
    // Techo (interiorLevel).
    if (this.scene.interiorLevel < target) {
      this.scene.interiorLevel = Math.min(target, this.scene.interiorLevel + inStep);
    } else {
      this.scene.interiorLevel = Math.max(target, this.scene.interiorLevel - roofOutStep);
    }
    // Overlay de oscuridad (overlayLevel): mismo fade-in, fade-out más rápido.
    if (this.scene.overlayLevel < target) {
      this.scene.overlayLevel = Math.min(target, this.scene.overlayLevel + inStep);
    } else {
      this.scene.overlayLevel = Math.max(target, this.scene.overlayLevel - overlayOutStep);
    }
    if (target === 0 && this.scene.interiorLevel < 0.002) {
      this.scene.interiorLevel = 0;
      this.scene.overlayLevel = 0;
      this.scene.interiorRevealRects = [];
    }

    // Fade de techos: atenuados proporcional a `interiorLevel` (el MISMO nivel que la oscuridad
    // del overlay) → techo y overlay entran y salen SIEMPRE sincronizados, a la misma velocidad
    // (fade-in y fade-out lentos/suaves). El resto de los techos quedan a 1.
    for (const img of this.scene.roofObjects.values()) {
      const inside = this.scene.interiorLevel > 0.002 && this.roofIntersectsInterior(img);
      img.setAlpha(inside ? 1 - (1 - GameScene.ROOF_DIM_ALPHA) * this.scene.interiorLevel : 1);
    }
  }

  /** True si la caja del sprite de techo (origin 0.5,1) se solapa con algún rect del interior. */
  public roofIntersectsInterior(img: Phaser.GameObjects.Image): boolean {
    const left = img.x - img.displayWidth / 2;
    const right = img.x + img.displayWidth / 2;
    const top = img.y - img.displayHeight;
    const bottom = img.y;
    for (const r of this.scene.interiorRevealRects) {
      if (left < r.x + r.w && right > r.x && top < r.y + r.h && bottom > r.y) {
        return true;
      }
    }
    return false;
  }

}
